import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, humanBytes, formatApiError } from "../lib/api";
import { Plus, FolderKanban, FileText } from "lucide-react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogTrigger,
} from "./ui/dialog";
import { toast } from "sonner";

/**
 * Customer-facing dashboard: a flat grid of project folders across all
 * customer workspaces linked to the current user. The user can also create
 * a new project (added to their first / personal workspace).
 */
export default function CustomerDashboard() {
    const navigate = useNavigate();
    const [workspaces, setWorkspaces] = useState(null);
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ name: "", description: "" });
    const [submitting, setSubmitting] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data: customers } = await api.get("/customers");
            setWorkspaces(customers || []);
            const all = await Promise.all(
                (customers || []).map(async (c) => {
                    try {
                        const { data } = await api.get(`/customers/${c.id}/projects`);
                        return data.map((p) => ({ ...p, customer_name: c.name }));
                    } catch {
                        return [];
                    }
                })
            );
            const flat = all.flat().sort(
                (a, b) => new Date(b.created_at) - new Date(a.created_at)
            );
            setProjects(flat);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const create = async (e) => {
        e.preventDefault();
        if (!workspaces?.length) {
            toast.error("Geen werkruimte beschikbaar.");
            return;
        }
        setSubmitting(true);
        try {
            await api.post(`/customers/${workspaces[0].id}/projects`, {
                name: form.name,
                description: form.description || null,
            });
            toast.success("Project aangemaakt");
            setOpen(false);
            setForm({ name: "", description: "" });
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="px-4 py-6 md:px-10 md:py-10 max-w-[1500px]" data-testid="customer-dashboard">
            <header className="flex items-end justify-between mb-8 md:mb-10 flex-wrap gap-3">
                <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">
                        01 — Mijn projecten
                    </div>
                    <h1 className="font-display text-4xl md:text-5xl tracking-tighter font-black leading-none">
                        Projecten
                    </h1>
                    <p className="text-zinc-500 mt-2 text-sm">
                        {projects.length} project{projects.length === 1 ? "" : "en"} — klik op een map
                        om bestanden toe te voegen.
                    </p>
                </div>
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild>
                        <Button
                            className="bg-[#0044FF] hover:bg-[#0033CC] rounded-md"
                            data-testid="customer-new-project-button"
                        >
                            <Plus className="w-4 h-4 mr-2" />
                            Nieuw project
                        </Button>
                    </DialogTrigger>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle className="font-display tracking-tight text-2xl">
                                Nieuw project
                            </DialogTitle>
                        </DialogHeader>
                        <form onSubmit={create} className="space-y-4" data-testid="customer-new-project-form">
                            <div>
                                <label className="text-xs uppercase tracking-[0.18em] text-zinc-500 mb-1.5 block">
                                    Naam *
                                </label>
                                <Input
                                    required
                                    value={form.name}
                                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                                    data-testid="customer-new-project-name"
                                />
                            </div>
                            <div>
                                <label className="text-xs uppercase tracking-[0.18em] text-zinc-500 mb-1.5 block">
                                    Beschrijving
                                </label>
                                <Textarea
                                    rows={3}
                                    value={form.description}
                                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                                    data-testid="customer-new-project-description"
                                />
                            </div>
                            <DialogFooter>
                                <Button
                                    type="submit"
                                    disabled={submitting}
                                    className="bg-[#0044FF] hover:bg-[#0033CC] rounded-md"
                                    data-testid="customer-new-project-submit"
                                >
                                    {submitting ? "Bezig…" : "Project aanmaken"}
                                </Button>
                            </DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>
            </header>

            {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {[1, 2, 3].map((i) => (
                        <div key={i} className="h-40 bg-white border border-zinc-200 rounded-md animate-pulse" />
                    ))}
                </div>
            ) : projects.length === 0 ? (
                <div className="border border-dashed border-zinc-300 rounded-md py-20 text-center bg-white">
                    <FolderKanban className="w-10 h-10 text-zinc-300 mx-auto mb-4" />
                    <div className="font-display text-2xl tracking-tight mb-2">
                        Nog geen projecten
                    </div>
                    <p className="text-sm text-zinc-500 mb-6 max-w-md mx-auto">
                        Maak een project aan om je foto's, video's en documenten te organiseren.
                        Onze AI beschrijft elk bestand automatisch zodat je later op woorden kan
                        zoeken.
                    </p>
                    <Button
                        onClick={() => setOpen(true)}
                        className="bg-[#0044FF] hover:bg-[#0033CC] rounded-md"
                        data-testid="customer-empty-cta"
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        Eerste project aanmaken
                    </Button>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {projects.map((p) => (
                        <button
                            key={p.id}
                            type="button"
                            onClick={() => navigate(`/projects/${p.id}`)}
                            className="group relative bg-white border border-zinc-200 rounded-md p-6 text-left hover:border-[#0044FF] hover:shadow-md hover:-translate-y-1 transition-all flex flex-col gap-3"
                            data-testid={`customer-project-card-${p.id}`}
                        >
                            <div className="flex items-start justify-between">
                                <div className="w-12 h-12 bg-zinc-100 border border-zinc-200 rounded-sm flex items-center justify-center">
                                    <FolderKanban className="w-6 h-6 text-zinc-700 group-hover:text-[#0044FF]" />
                                </div>
                                <span className="text-[10px] font-mono uppercase text-zinc-500">
                                    {new Date(p.created_at).toLocaleDateString("nl-BE")}
                                </span>
                            </div>
                            <div>
                                <div className="font-display text-xl font-bold tracking-tight leading-tight">
                                    {p.name}
                                </div>
                                {p.description && (
                                    <p className="text-sm text-zinc-600 mt-1 line-clamp-2">
                                        {p.description}
                                    </p>
                                )}
                            </div>
                            <div className="flex items-center justify-between text-xs text-zinc-500 pt-3 border-t border-zinc-100 mt-auto">
                                <span className="inline-flex items-center gap-1">
                                    <FileText className="w-3 h-3" /> {p.file_count} bestanden
                                </span>
                                <span className="font-mono">{humanBytes(p.total_size_bytes)}</span>
                            </div>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
